import { defineStore } from 'pinia';
import api from '../lib/api.js';

/**
 * Angemeldeter Benutzer und seine Rechte.
 *
 * `ready` unterscheidet "noch nicht geprüft" von "nicht angemeldet" —
 * ohne diese Unterscheidung blitzt beim Laden kurz die Anmeldemaske auf,
 * bevor die Sitzung bestätigt ist.
 */
export const useAuthStore = defineStore('auth', {
    state: () => ({
        user: null,
        permissions: [],
        ready: false,
    }),
    getters: {
        isAuthenticated: (s) => !!s.user,
        can: (s) => (perm) => s.permissions.includes(perm),
        canAny: (s) => (perms) => (perms || []).some((p) => s.permissions.includes(p)),
        /**
         * Adresse des Profilbilds.
         *
         * Der Server speichert den Pfad relativ zu `public/uploads/`, die
         * Adresse ist also `/uploads/{pfad}`. Der Zufallsanteil im Dateinamen
         * sorgt dafür, dass ein Wechsel sofort sichtbar wird — sonst zeigte
         * der Browser weiter das alte Bild aus seinem Zwischenspeicher.
         */
        photoUrl: (s) => {
            const p = s.user?.photo_path;
            return p ? `/uploads/${p}` : '';
        },
        displayName: (s) => {
            if (!s.user) return '';
            return [s.user.first_name, s.user.last_name].filter(Boolean).join(' ') || s.user.email;
        },
    },
    actions: {
        async refresh() {
            try {
                const { data } = await api.get('/me', { _silent: true });
                this.user = data.user;
                this.permissions = data.permissions || [];
            } catch {
                this.user = null;
                this.permissions = [];
            } finally {
                this.ready = true;
            }
        },
        async login(email, password) {
            const { data } = await api.post('/login', { email, password }, { _silent: true });
            this.user = data.user;
            this.permissions = data.permissions || [];
            this.ready = true;
        },
        /**
         * Sitzung übernehmen, die auf anderem Weg entstanden ist.
         *
         * Die Passkey-Anmeldung liefert denselben Rumpf wie `/login`, geht
         * aber über einen eigenen Endpunkt. Ohne diese Stelle müsste die
         * Anmeldemaske die Zustandsfelder selbst setzen — und der Speicher
         * hätte zwei Besitzer.
         */
        adoptSession(data) {
            this.user = data.user;
            this.permissions = data.permissions || [];
            this.ready = true;
        },
        async logout() {
            try { await api.post('/logout'); } finally {
                this.user = null;
                this.permissions = [];
            }
        },
    },
});
